import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import _ from 'lodash';

import { PermsSerialized, AuthenticatorConfigSerialized, SuccessfulLogin } from '@playq/octopus2-auth';

import { systemRehydrate } from '/store/systemActions';
import { getPendingActionType } from '/store/helpers';

import { IAuthState, initialState, IUserInfo } from './state';
import { AuthAction } from './types';
import { authSelectors } from './selectors';

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    updateUser: (state, { payload }: PayloadAction<IUserInfo>) => {
      state.user = {
        ...state.user,
        ...payload,
      };
    },
    rehydrate: (state, { payload }: PayloadAction<IAuthState>) => {
      _.merge(state, payload);
    },
    reset: (state) => {
      _.merge(state, initialState, {
        googleRedirectURL: state.googleRedirectURL,
      });
    },
    getPermsByCompany: (state, { payload }: PayloadAction<PermsSerialized>) => {
      state.companyPerms = payload;
    },
    updateRememberMe: (state, { payload }: PayloadAction<boolean>) => {
      state.rememberMe = payload;
    },
    updateGoogleRedirectURL: (state, { payload }: PayloadAction<string | undefined>) => {
      return {
        ...state,
        googleRedirectURL: payload,
      };
    },
    updateLoginRedirectURL: (state, { payload }: PayloadAction<string | undefined>) => {
      return {
        ...state,
        loginRedirectURL: payload === '/login' || payload === '/auth/google' ? state.loginRedirectURL : payload,
      };
    },
    updateServicesAuthorizationStatus: (state, { payload }: PayloadAction<boolean>) => ({
      ...state,
      isServicesAuthorizationSet: payload,
    }),
    updateMfa: (state, { payload }: PayloadAction<{ [key: string]: AuthenticatorConfigSerialized } | undefined>) => ({
      ...state,
      mfa: payload,
    }),
  },
  extraReducers: (builder) => {
    builder.addCase(systemRehydrate.type, (state) => {
      state.pending = true;
    });
    builder.addCase(
      getPendingActionType(AuthAction.Login),
      (state, action: PayloadAction<undefined, string, { arg: SuccessfulLogin }>) => {
        _.merge(state, {
          loggedIn: true,
          ...action.meta.arg.serialize(),
        });
      }
    );
    builder.addCase(getPendingActionType(AuthAction.Logout), (state) => {
      _.merge(state, initialState, {
        rememberMe: state.rememberMe,
        googleRedirectURL: state.googleRedirectURL,
      });
    });
  },
});

export const authToolkit = Object.freeze({
  initialState,
  actions: authSlice.actions,
  selectors: authSelectors,
});

export const authReducer = authSlice.reducer;
